/**
 * <ConfigurableReportPage /> — the single page component every
 * report in the 157-report catalog renders through per
 * M3X.REPORTS §3.1.
 *
 * Per-report config supplies:
 *   - title + description + domain + agent + AI tier + duration
 *   - columns[] for the DataTable + export framework
 *   - filters[] for the filter chip row
 *   - charts[] (optional)
 *   - endpoint URL + response shape
 *   - article citation (optional, compliance reports)
 *
 * Wires together every PR 2 + PR 3 primitive:
 *   - ReportPage shell (header / strip / cadence / actions row)
 *   - ReportAIInsight panel at top (tier-routed)
 *   - <DataTable> with cell-rendering accessor support
 *   - export-as-is via the lib/export framework (DataTable handles)
 *   - zoom controls via useReportZoom
 *   - progress framework via lib/report-progress
 */

import {
  type ColumnDef,
  createColumnHelper,
} from "@tanstack/react-table";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { type ReactNode, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { authHeaders } from "../../lib/api";
import { useReportZoom } from "../../lib/report-zoom";
import type {
  AIInsightTier,
  ReportAgent,
  ReportCategory,
  ReportDomain,
} from "../../lib/reports/catalog";
import { DataTable } from "./DataTable";
import { PageHeader } from "./PageHeader";
import { ReportAIInsight } from "./ReportAIInsight";

/* eslint-disable @typescript-eslint/no-explicit-any */

export type ReportColumnType =
  | "string"
  | "number"
  | "currency"
  | "date"
  | "boolean";

export interface ReportColumnConfig {
  /** Stable key — matches the API response field. */
  key: string;
  /** Localized header label (already-translated string OR i18n key
   * resolved by the caller). */
  label: string;
  /** Drives DataTable + export framework cell coercion. */
  type: ReportColumnType;
  /** Optional currency code for currency cells (default SAR). */
  currency?: string;
  /** Optional decimal count for number / currency cells. */
  decimals?: number;
  /** Optional cell render override (e.g. for badges). */
  cell?: (value: any, row: any) => ReactNode;
}

export interface ReportConfig<TRow = any> {
  /** Stable URL slug — matches lib/reports/catalog manifest. */
  id: string;
  /** Localized title (already-translated). */
  title: string;
  /** Optional localized description. */
  description?: string;
  /** Domain bucket — drives header color strip. */
  domain: ReportDomain;
  /** Owning agent for AI insight panel. */
  agent: ReportAgent;
  /** Tier dispatch for the AI insight (sonnet / haiku / template). */
  aiTier: AIInsightTier;
  /** Category — drives chip color on cards. */
  category?: ReportCategory;
  /** Optional KSA Labor Law article citation. */
  article?: string;
  /** Backend endpoint that returns the row data. Caller passes the
   * full URL including query params. */
  endpoint: string;
  /** Columns the DataTable renders + the export framework emits. */
  columns: ReportColumnConfig[];
  /** Optional KPI block above the table. */
  kpiBuilder?: (rows: TRow[]) => ReactNode;
  /** Optional chart block above the table. */
  chartBuilder?: (rows: TRow[]) => ReactNode;
  /** Optional facts compiler for the AI insight. */
  factsBuilder?: (rows: TRow[]) => Record<string, string | number>;
  /** Optional period label for the AI insight + export filename. */
  period?: string;
}

export interface ConfigurableReportPageProps<TRow = any> {
  config: ReportConfig<TRow>;
  /** Optional override of the i18n locale for the AI insight (the
   * config consumes `i18n.language` by default). */
  localeOverride?: string;
}

const DOMAIN_STRIP: Record<ReportDomain, string> = {
  dashboards: "from-brand-blue to-brand-purple",
  leaves: "from-emerald-400 to-emerald-600",
  payroll: "from-brand-deep to-brand-blue",
  loans: "from-brand-blue to-brand-purple",
  excuses: "from-amber-400 to-amber-600",
  attendance: "from-brand-teal to-brand-blue",
  eos: "from-brand-purple to-brand-deep",
  travel: "from-sky-400 to-sky-600",
  comp: "from-indigo-400 to-indigo-600",
  benefits: "from-rose-400 to-pink-500",
  employee: "from-brand-blue to-brand-teal",
  org: "from-violet-400 to-violet-600",
  analytics: "from-brand-teal to-brand-purple",
  compliance: "from-rose-500 to-rose-700",
  agents: "from-brand-teal to-brand-blue",
};

export function ConfigurableReportPage<TRow = any>({
  config,
  localeOverride,
}: ConfigurableReportPageProps<TRow>) {
  const { t, i18n } = useTranslation();
  const locale = localeOverride ?? i18n.language ?? "en";
  const contentRef = useRef<HTMLDivElement | null>(null);

  // ---- Zoom controller ------------------------------------------------
  const { zoom, zoomIn, zoomOut, reset, onWheel } = useReportZoom({
    userId: "_global", // PR 14-18 will switch to Clerk user id
    reportId: config.id,
  });

  // ---- Data fetch -----------------------------------------------------
  // Auth-bridged fetch: pulls the Clerk Bearer token + X-Tenant-Slug
  // from the shared lib/api helper. Raw fetch() would 401 against the
  // production tenant resolver and the page would silently render an
  // empty table.
  const dataQuery = useQuery({
    queryKey: ["report", config.id, config.endpoint],
    queryFn: async () => {
      const headers = await authHeaders();
      const res = await fetch(config.endpoint, { headers });
      if (!res.ok) {
        throw new Error(`Report ${config.id} fetch ${res.status}`);
      }
      const body = await res.json();
      // The endpoint may return `{rows: [...]}` or a bare array.
      return (Array.isArray(body) ? body : (body.rows ?? body.items ?? [])) as TRow[];
    },
  });
  const rows = dataQuery.data ?? [];
  const isLoading = dataQuery.isLoading;

  // ---- Column adaptation for DataTable -------------------------------
  // If the config supplies columns, use them. Otherwise infer from
  // the first row at runtime — every backend aggregator returns
  // plain dict rows, so this works for the generic case.
  const ch = createColumnHelper<any>();
  const effectiveColumns = useMemo<ReportColumnConfig[]>(() => {
    if (config.columns.length > 0) return config.columns;
    if (rows.length === 0) return [];
    const first = rows[0] as Record<string, unknown>;
    return Object.keys(first).map((k) => ({
      key: k,
      label: _humanizeKey(k),
      type: _inferType(first[k]),
    }));
  }, [config.columns, rows.length]);

  const columns = useMemo<ColumnDef<any, any>[]>(
    () =>
      effectiveColumns.map((col) =>
        ch.accessor(col.key as any, {
          id: col.key,
          header: col.label,
          cell: col.cell
            ? (info) => col.cell!(info.getValue(), info.row.original)
            : (info) => _defaultCellRender(info.getValue(), col),
        }),
      ),
    [effectiveColumns],
  );

  const stripClass = DOMAIN_STRIP[config.domain];
  const summaryFacts = config.factsBuilder
    ? config.factsBuilder(rows)
    : {};

  return (
    <div className="flex flex-col gap-5">
      <div
        aria-hidden
        className={`h-1 w-full rounded-full bg-gradient-to-r ${stripClass}`}
      />

      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title={config.title}
          subtitle={config.description}
          breadcrumbs={[
            { label: t("reports.breadcrumb_root", "Reports"), href: "/reports" },
            {
              label: t(`reports.domain_${config.domain}_title`, config.domain),
              href: `/reports/${config.domain}`,
            },
            { label: config.title },
          ]}
        />
        <ZoomToolbar
          zoom={zoom}
          onIn={zoomIn}
          onOut={zoomOut}
          onReset={reset}
        />
      </div>

      <ReportAIInsight
        context={{
          reportId: config.id,
          domain: config.domain,
          tier: config.aiTier,
          agent: config.agent,
          locale,
          title: config.title,
          period: config.period,
          summaryFacts,
          article: config.article,
        }}
      />

      <div
        ref={contentRef}
        onWheel={onWheel}
        style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
        className="flex flex-col gap-4"
      >
        {config.kpiBuilder && rows.length > 0 && (
          <section
            className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
            aria-label={t("report_page.kpi_label", "Key metrics")}
          >
            {config.kpiBuilder(rows)}
          </section>
        )}
        {config.chartBuilder && rows.length > 0 && (
          <section
            className="rounded-xl border border-border-subtle bg-surface-default p-4"
            aria-label={t("report_page.chart_label", "Chart")}
          >
            {config.chartBuilder(rows)}
          </section>
        )}
        <section
          className="rounded-xl border border-border-subtle bg-surface-default p-2"
          aria-label={t("report_page.table_label", "Detail table")}
        >
          <DataTable
            columns={columns}
            data={rows as any[]}
            loading={isLoading}
            exportFilename={config.title}
            hideBulkSelection
          />
        </section>
      </div>
    </div>
  );
}

function ZoomToolbar({
  zoom,
  onIn,
  onOut,
  onReset,
}: {
  zoom: number;
  onIn: () => void;
  onOut: () => void;
  onReset: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center gap-1 rounded-sm border border-border-subtle bg-surface-default px-2 py-1"
      role="group"
      aria-label={t("reports.zoom_aria", "Zoom controls")}
    >
      <button
        type="button"
        onClick={onOut}
        aria-label={t("reports.zoom_out", "Zoom out")}
        className="rounded p-1 text-text-tertiary hover:bg-surface-sunken hover:text-text-primary"
      >
        <Minus size={14} aria-hidden />
      </button>
      <span className="min-w-[40px] text-center text-xs text-text-secondary">
        {Math.round(zoom * 100)}%
      </span>
      <button
        type="button"
        onClick={onIn}
        aria-label={t("reports.zoom_in", "Zoom in")}
        className="rounded p-1 text-text-tertiary hover:bg-surface-sunken hover:text-text-primary"
      >
        <Plus size={14} aria-hidden />
      </button>
      <button
        type="button"
        onClick={onReset}
        aria-label={t("reports.zoom_reset", "Reset zoom")}
        className="rounded p-1 text-text-tertiary hover:bg-surface-sunken hover:text-text-primary"
      >
        <RotateCcw size={14} aria-hidden />
      </button>
    </div>
  );
}

function _humanizeKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function _inferType(value: unknown): ReportColumnType {
  if (value instanceof Date) return "date";
  if (typeof value === "number") return "number";
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}(T|$)/.test(value)) {
    return "date";
  }
  return "string";
}

function _defaultCellRender(value: unknown, col: ReportColumnConfig): ReactNode {
  if (value === null || value === undefined) return "—";
  switch (col.type) {
    case "currency": {
      const n = typeof value === "number" ? value : Number(value);
      if (Number.isNaN(n)) return String(value);
      return `${n.toLocaleString(undefined, {
        minimumFractionDigits: col.decimals ?? 2,
        maximumFractionDigits: col.decimals ?? 2,
      })} ${col.currency ?? "SAR"}`;
    }
    case "number": {
      const n = typeof value === "number" ? value : Number(value);
      if (Number.isNaN(n)) return String(value);
      return n.toLocaleString(undefined, {
        minimumFractionDigits: col.decimals,
        maximumFractionDigits: col.decimals,
      });
    }
    case "date": {
      if (value instanceof Date) return value.toISOString().slice(0, 10);
      if (typeof value === "string") return value.slice(0, 10);
      return String(value);
    }
    case "boolean":
      return value ? "✓" : "—";
    default:
      return String(value);
  }
}

export default ConfigurableReportPage;
